// Navbar.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  Icon,
  Tooltip,
  Text,
  Flex,
  Image,
  Divider,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
  Spacer,
  useDisclosure,
  useBreakpointValue,
  HStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  IconButton,
  keyframes,
} from '@chakra-ui/react';
import { LuMenu } from 'react-icons/lu';
import {
  LuTrendingUp,
  LuBarChart3,
  LuAreaChart,
  LuSmile,
  LuGlobe2,
  LuLightbulb,
  LuMessageSquarePlus,
  LuBookOpen,
  LuListTodo,
  LuLayoutDashboard,
  LuPieChart,
  LuSearch,
  LuRocket,
  LuMegaphone,
  LuStar,
  LuTarget,
  LuArrowLeftRight,
  LuCalculator,
  LuCalendarRange,
  LuDollarSign,
  LuImage,
  LuBot,
  LuShield,
  LuKeyRound,
  LuScale,
} from 'react-icons/lu';
import { HiUser } from 'react-icons/hi2';
import { useLocation, useNavigate } from 'react-router-dom';
import UserSettingsModal from './UserSettingsModal';
import { useUser } from '../contexts/UserContext';

// Google Data - Core business metrics from Google
const GOOGLE_DATA_ITEMS = [
  { 
    icon: LuTrendingUp, 
    label: 'Review Growth', 
    path: '/dashboards/gross-growth', 
    description: 'Growth trends over time' 
  },
  { 
    icon: LuBarChart3, 
    label: 'Total Reviews', 
    path: '/dashboards/total-reviews', 
    description: 'Review volume & ratings' 
  },
  { 
    icon: LuSmile, 
    label: 'Sentiment Analysis', 
    path: '/dashboards/sentiment', 
    description: 'Customer satisfaction' 
  },
  {
    icon: LuMegaphone,
    label: 'Google Ads',
    path: '/dashboards/ads-intelligence',
    description: 'Ad campaigns & strategies'
  },
];

// Market Analysis - Business insights and competitive views
const MARKET_ITEMS = [
  {
    icon: LuGlobe2,
    label: 'Competitor Map',
    path: '/dashboards/competitor-map',
    description: 'Geographic competitor view'
  },
  {
    icon: LuLightbulb,
    label: 'Business Insights',
    path: '/dashboards/business-insights',
    description: 'Complete business overview'
  },
  {
    icon: LuCalculator,
    label: 'Localytics Ranking',
    path: '/dashboards/localytics-score',
    description: 'Performance ranking breakdown'
  },
  {
    icon: LuDollarSign,
    label: 'Pricing Analysis',
    path: '/dashboards/pricing',
    description: 'Market pricing & value ranking'
  },
  {
    icon: LuCalendarRange,
    label: 'Month in Review',
    path: '/dashboards/market-trends',
    description: 'Market-wide customer insights'
  },
];

// Digital Presence - Online visibility
const DIGITAL_ITEMS = [
  {
    icon: LuAreaChart,
    label: 'Traffic',
    path: '/dashboards/keyword-intelligence',
    description: 'Website analytics'
  },
  {
    icon: LuScale,
    label: 'Organic vs Paid',
    path: '/dashboards/organic-paid-traffic',
    description: 'Organic & paid traffic comparison'
  },
  {
    icon: LuSearch,
    label: 'SEO',
    path: '/dashboards/web-traffic',
    description: 'Search performance'
  },
  {
    icon: LuKeyRound,
    label: 'Keywords',
    path: '/dashboards/keywords',
    description: 'Keyword rankings & opportunities'
  },
];

// Strategy - Planning and actions
const STRATEGY_ITEMS = [
  {
    icon: LuDollarSign,
    label: 'Revenue Opportunity',
    path: '/dashboards/revenue-opportunity',
    description: 'Model your market size, share & revenue goal'
  },
  {
    icon: LuTarget,
    label: 'Set Goals',
    path: '/playbook/goals',
    description: 'Set targets for reviews & traffic'
  },
  {
    icon: LuListTodo,
    label: 'Action Items',
    path: '/playbook/actions',
    description: 'Tasks to complete'
  },
  {
    icon: LuArrowLeftRight,
    label: 'Compare Businesses',
    path: '/compare-year',
    description: 'Side-by-side analysis'
  },
  {
    icon: LuImage,
    label: 'Content Templates',
    path: '/dashboards/content-templates',
    description: 'AI-generated marketing visuals'
  },
  {
    icon: LuBookOpen,
    label: 'Reading List',
    path: '/playbook/reading',
    description: 'Curated articles & resources'
  },
];


const favoriteStarPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(251, 191, 36, 0));
  }
  50% {
    transform: scale(1.35);
    filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.9));
  }
`;

function Navbar({ onChatOpen }) {
  const [isExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const activeColor = 'blue.500';
  const inactiveColor = useColorModeValue('gray.600', 'gray.400');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const { displayName, initials, email, isAgencyOwner, agencyBranding, justFavorited } = useUser();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const brandedMarketItems = useMemo(() => {
    if (!agencyBranding?.agency_name) return MARKET_ITEMS;
    return MARKET_ITEMS.map(item =>
      item.label === 'Localytics Ranking'
        ? { ...item, label: `${agencyBranding.agency_name} Ranking` }
        : item
    );
  }, [agencyBranding]);

  const isActivePath = (path) => location.pathname === path;
  const isActiveSection = (paths) => paths.some(path => location.pathname.startsWith(path));

  const handleMenuItemClick = (path) => {
    navigate(path);
  };

  const NavItem = ({ icon, label, path, subItems, onClick, highlight }) => {
    const isActive = path ? isActivePath(path) : 
                     subItems ? isActiveSection(subItems.map(item => item.path)) : false;

    if (subItems) {
      return (
        <Menu placement="right-start" isLazy offset={[15, 0]}>
          {({ isOpen: menuIsOpen }) => (
            <>
              <Tooltip 
                label={label} 
                placement="right" 
                isDisabled={isExpanded || menuIsOpen}
                hasArrow
              >
                <MenuButton
                  as={Button}
                  w="full"
                  h="auto"
                  p={3}
                  variant="ghost"
                  borderRadius="md"
                  cursor="pointer"
                  color={isActive ? activeColor : inactiveColor}
                  bg={isActive ? hoverBgColor : 'transparent'}
                  _hover={{ bg: hoverBgColor }}
                  _active={{ bg: hoverBgColor }}
                  transition="all 0.2s"
                >
                  <Flex align="center" justify={isExpanded ? "flex-start" : "center"} w="full">
                    <Icon as={icon} boxSize={{ base: 7, md: 5 }} />
                    {isExpanded && <Text ml={3}>{label}</Text>}
                  </Flex>
                </MenuButton>
              </Tooltip>
              <Portal>
                <MenuList 
                  ml={2} 
                  minW="260px"
                  bg={bgColor}
                  borderColor={borderColor}
                  boxShadow="lg"
                  zIndex={1100}
                  py={2}
                >
                  {subItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() => handleMenuItemClick(item.path)}
                      color={isActivePath(item.path) ? activeColor : inactiveColor}
                      bg={isActivePath(item.path) ? hoverBgColor : 'transparent'}
                      _hover={{ bg: hoverBgColor }}
                      py={3}
                      role="group"
                    >
                      <Flex width="100%" align="flex-start">
                        <Icon 
                          as={item.icon} 
                          boxSize={{ base: 5, md: 4 }} 
                          mr={3}
                          mt="2px"
                          color={isActivePath(item.path) ? activeColor : inactiveColor}
                          _groupHover={{ color: activeColor }}
                        />
                        <Box>
                          <Text 
                            fontWeight={isActivePath(item.path) ? "semibold" : "medium"}
                            _groupHover={{ color: activeColor }}
                            lineHeight="1.2"
                            fontSize="md"
                          >
                            {item.label}
                          </Text>
                          <Text 
                            fontSize="xs" 
                            color="gray.500" 
                            mt={0.5}
                            _groupHover={{ color: isActivePath(item.path) ? activeColor : "gray.600" }}
                          >
                            {item.description}
                          </Text>
                        </Box>
                      </Flex>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </>
          )}
        </Menu>
      );
    }

    return (
      <Tooltip
        label={label}
        placement="right"
        isDisabled={isExpanded}
        hasArrow
      >
        <Button
          onClick={onClick || (path ? () => handleMenuItemClick(path) : undefined)}
          w="full"
          h="auto"
          p={3}
          variant="ghost"
          borderRadius="md"
          cursor="pointer"
          color={highlight ? 'yellow.400' : isActive ? activeColor : inactiveColor}
          bg={highlight ? 'yellow.50' : isActive ? hoverBgColor : 'transparent'}
          _hover={{ bg: hoverBgColor }}
          _active={{ bg: hoverBgColor }}
          transition="all 0.2s"
          leftIcon={
            <Icon
              as={icon}
              boxSize={{ base: 7, md: 5 }}
              {...(highlight && {
                color: 'yellow.400',
                animation: `${favoriteStarPulse} 0.8s ease-in-out 3`,
              })}
            />
          }
          justifyContent={isExpanded ? "flex-start" : "center"}
        >
          {isExpanded && label}
        </Button>
      </Tooltip>
    );
  };

  const sidebarContent = (
    <VStack spacing={4} align="stretch" h="full">
      <Box
        as="button"
        onClick={() => { navigate('/home'); if (isMobile) onDrawerClose(); }}
        px={4}
        py={2}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        _hover={{ bg: hoverBgColor, transform: 'scale(1.05)' }}
        borderRadius="md"
        transition="all 0.2s"
        gap={1}
      >
        <Image
          src={agencyBranding?.logo_url || "/output.png"}
          alt={agencyBranding?.agency_name ? `${agencyBranding.agency_name} Logo` : "Main Logo"}
          height="40px"
          width="auto"
          maxWidth="56px"
          mx="auto"
          objectFit="contain"
        />
      </Box>

      <Tooltip label={`${displayName}\n${email}`} placement="right" hasArrow isDisabled={isMobile}>
        <Button
          onClick={onOpen}
          variant="ghost"
          w="full"
          h="auto"
          py={3}
          px={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
          borderRadius="md"
          color={inactiveColor}
          _hover={{ bg: hoverBgColor, color: activeColor }}
          _active={{ bg: hoverBgColor }}
          transition="all 0.2s"
        >
          <Flex
            w="36px"
            h="36px"
            borderRadius="full"
            border="2px solid"
            borderColor={isAgencyOwner ? "blue.400" : "currentColor"}
            justify="center"
            align="center"
            bg="transparent"
            overflow="hidden"
            position="relative"
            {...(isAgencyOwner && {
              boxShadow: "0 0 8px rgba(66, 153, 225, 0.6), 0 0 16px rgba(66, 153, 225, 0.4), 0 0 24px rgba(66, 153, 225, 0.2)",
              animation: "agencyGlow 2s ease-in-out infinite alternate",
            })}
            sx={isAgencyOwner ? {
              "@keyframes agencyGlow": {
                "0%": { boxShadow: "0 0 6px rgba(66, 153, 225, 0.4), 0 0 12px rgba(66, 153, 225, 0.2)" },
                "100%": { boxShadow: "0 0 10px rgba(66, 153, 225, 0.7), 0 0 20px rgba(66, 153, 225, 0.4), 0 0 30px rgba(66, 153, 225, 0.2)" },
              },
            } : undefined}
          >
            <Icon as={HiUser} boxSize={6} position="absolute" bottom="-1px" color={isAgencyOwner ? "blue.400" : undefined} />
          </Flex>
          <Text fontSize="2xs" fontWeight="medium" noOfLines={1} maxW="56px" opacity={0.9}>
            {displayName ? displayName.split(' ')[0] : 'User'}
          </Text>
        </Button>
      </Tooltip>

      {isAgencyOwner && (
        <Box px={2}>
          <Tooltip label="Admin Dashboard" placement="right" hasArrow isDisabled={isMobile}>
            <Button
              onClick={() => handleMenuItemClick('/agency/admin')}
              variant="ghost"
              w="full"
              h="auto"
              py={3}
              px={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              borderRadius="md"
              color={isActivePath('/agency/admin') ? activeColor : "blue.300"}
              bg={isActivePath('/agency/admin') ? hoverBgColor : 'transparent'}
              _hover={{ bg: hoverBgColor, color: "blue.400" }}
              _active={{ bg: hoverBgColor }}
              transition="all 0.2s"
            >
              <Icon
                as={LuShield}
                boxSize={{ base: 7, md: 5 }}
                color="blue.300"
                filter="drop-shadow(0 0 4px rgba(66, 153, 225, 0.7))"
              />
              <Text
                fontSize="2xs"
                fontWeight="bold"
                color="blue.300"
                textShadow="0 0 6px rgba(66, 153, 225, 0.8), 0 0 12px rgba(66, 153, 225, 0.5)"
                lineHeight="1.2"
                letterSpacing="0.05em"
              >
                ADMIN
              </Text>
            </Button>
          </Tooltip>
        </Box>
      )}

      <Divider my={2} borderColor={borderColor} />

      <VStack spacing={1} align="stretch" px={2}>
        <NavItem icon={LuLayoutDashboard} label="Google Data" subItems={GOOGLE_DATA_ITEMS} />
        <NavItem icon={LuPieChart} label="Market Analysis" subItems={brandedMarketItems} />
        <NavItem icon={LuGlobe2} label="Digital Presence" subItems={DIGITAL_ITEMS} />
        <NavItem icon={LuRocket} label="Strategy" subItems={STRATEGY_ITEMS} />
        <NavItem icon={LuStar} label="My Favorite" path="/dashboards/favorites" highlight={justFavorited} />
        <Divider my={2} borderColor={borderColor} />
        <NavItem icon={LuMessageSquarePlus} label="Feedback & Roadmap" path="/roadmap" />
        <NavItem icon={LuBot} label="AI Chat" path="/chat" />
      </VStack>

      <Spacer />
    </VStack>
  );

  if (isMobile) {
    return (
      <>
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          bg={bgColor}
          borderTop="1px"
          borderColor={borderColor}
          zIndex={1000}
          px={2}
          py={1}
          boxShadow="0 -2px 10px rgba(0,0,0,0.05)"
        >
          <HStack justify="space-around" spacing={0}>
            {[
              { icon: LuLayoutDashboard, label: 'Home', path: '/home' },
              { icon: LuPieChart, label: 'Market', path: '/dashboards/business-insights' },
              { icon: LuGlobe2, label: 'Digital', path: '/dashboards/keyword-intelligence' },
              { icon: LuRocket, label: 'Strategy', path: '/playbook/goals' },
              { icon: LuMenu, label: 'More', path: null },
            ].map((item) => {
              const isActive = item.path && location.pathname === item.path;
              return (
                <Button
                  key={item.label}
                  variant="ghost"
                  size="sm"
                  h="auto"
                  py={1.5}
                  px={2}
                  minW={0}
                  minH={0}
                  flexDirection="column"
                  gap={0.5}
                  color={isActive ? activeColor : inactiveColor}
                  onClick={() => {
                    if (item.path) navigate(item.path);
                    else onDrawerOpen();
                  }}
                  _hover={{ bg: hoverBgColor }}
                >
                  <Icon as={item.icon} boxSize={5} />
                  <Text fontSize="2xs" fontWeight={isActive ? "semibold" : "normal"}>{item.label}</Text>
                </Button>
              );
            })}
          </HStack>
        </Box>

        <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose} size="xs">
          <DrawerOverlay />
          <DrawerContent bg={bgColor}>
            <DrawerCloseButton />
            <DrawerBody px={2} py={4}>
              {sidebarContent}
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <UserSettingsModal isOpen={isOpen} onClose={onClose} />
      </>
    );
  }

  return (
    <>
      <Box
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        w="72px"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        transition="all 0.2s"
        zIndex={1000}
        py={4}
        overflowX="hidden"
        overflowY="auto"
        boxShadow="sm"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: borderColor, borderRadius: '24px' },
        }}
      >
        {sidebarContent}
      </Box>
      <UserSettingsModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}

export default Navbar;